import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../pages/registration/registrationPage.css';
import { useUser } from '../../contexts/userContext';
import { useApi } from '../../shared/api';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectLoggedUser, updateProfileCompletedTag
} from '../../redux/slices/auth';
import { userRegisterationEvent } from "../../shared/events/login.event";

// import '../../pages/registration/registrationPage.css'
import './style/registerUser.css'

interface RegisterProps {
    setCurrentView?: (view: "login" | "register" | "verifyOtp") => void;
    handleLogin?: (isRedirect: boolean, userId: number, mobileNo: string) => void;
    isRegistrationCompleted?: (bool: boolean) => void;
    origin?: 'login' | 'register' | 'verifyOtp' | 'loginPage';
}

const pushUserRegistrationEvent = (formData: {
    name: string;
    mobile_no: string;
    email: string;
    referral_code?: string;
}) => {
    userRegisterationEvent({
        name: formData.name,
        number: Number(formData.mobile_no),
        email: formData.email,
        code: formData.referral_code || ""
    });
};


const Register = ({ setCurrentView, handleLogin, isRegistrationCompleted, origin }: RegisterProps) => {
    const API = useApi();
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const loggedUser = useSelector(selectLoggedUser);
    const location = useLocation();
    const { utm_session_id, city_pincode } = useUser();
    const nameInputRef = useRef<HTMLInputElement>(null);
    const [mobileNo, setMobileNo] = useState<any | null>(null);
    const [refCode, setRefCode] = useState('');
    const [loading, setLoading] = useState(false); // New loading state
    const query = new URLSearchParams(location.search);
    const authInfo = useSelector(selectLoggedUser)
    const returnUrl = location.state?.returnUrl;
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile_no: '',
        referral_code: '',
    });

    const [errors, setErrors] = useState({
        name: '',
        email: '',
        mobile_no: '',
        referral_code: '',
    });

    const [error, setError] = useState<string | null>(null);

    let utmQuery = location.state?.utmQuery
    utmQuery = utmQuery || localStorage.getItem('utm_query_data')

    if (utm_session_id) {
        query.set('utm_session_id', utm_session_id);
    }

    useEffect(() => {
        const rawMobile = sessionStorage.getItem("mobileNumber");
        const cleanMobile = rawMobile?.replace(/"/g, "") || "";

        if (cleanMobile) {
            setMobileNo(cleanMobile);
            setFormData(prev => ({
                ...prev,
                mobile_no: prev.mobile_no || cleanMobile, // only prefill if not already filled
            }));
        }
        const ref_code = sessionStorage.getItem("setReferralCode") || ""; // Default to an empty string
        setRefCode(ref_code);
        setFormData(prevData => ({
            ...prevData,
            referral_code: ref_code || prevData.referral_code, // Prevent overwriting if user already filled it
        }));

    }, []);

    const isValidForm = () => {
        const isNameValid = formData.name.trim().length >= 2;
        const isEmailValid = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(formData.email);
        const isMobileValid = formData.mobile_no.length === 10;
        return isNameValid && isEmailValid && isMobileValid;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let updatedValue = value.trimStart();

        switch (name) {
            case 'name':
                updatedValue = updatedValue.replace(/[^a-zA-Z\s]/g, ''); // Only alphabets and spaces

                // Check if the value is empty or contains only spaces
                if (updatedValue.trim() === '') {
                    setErrors((prev) => ({ ...prev, name: 'Please enter the name' }));
                } else if (updatedValue.length > 50) {
                    setErrors((prev) => ({ ...prev, name: 'Name cannot exceed 50 characters' }));
                } else if (updatedValue.trim().length < 2) {
                    setErrors((prev) => ({ ...prev, name: 'Please enter at least 2 characters' }));
                } else {
                    setErrors((prev) => ({ ...prev, name: '' })); // Clear errors if valid
                }
                break;
            case 'email':
                updatedValue = updatedValue.replace(/\s/g, ''); // Remove spaces entirely

                if (updatedValue.trim() === '') {
                    setErrors((prev) => ({ ...prev, email: 'Please enter a valid email id' }));
                } else if (!/^[a-zA-Z0-9@._-]+$/.test(updatedValue)) {
                    setErrors((prev) => ({
                        ...prev,
                        email: 'Only alphabets, numbers, and special characters (@, ., -, _) are allowed',
                    }));
                } else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(updatedValue)) {
                    setErrors((prev) => ({ ...prev, email: 'Please enter a valid email id' }));
                } else if (/^[0-9]+@/.test(updatedValue)) {
                    // Check if the part before @ is only numeric
                    setErrors((prev) => ({ ...prev, email: 'The part before @ cannot be only numeric' }));
                }
                else if (updatedValue.length < 10) {
                    setErrors((prev) => ({ ...prev, email: 'Please enter a valid email id' }));
                } else {
                    setErrors((prev) => ({ ...prev, email: '' })); // Clear error
                }
                break;

            case 'mobile_no':
                updatedValue = updatedValue.replace(/[^0-9]/g, '').slice(0, 14); // Only numbers, max length 10
                if (!updatedValue) {
                    setErrors((prev) => ({ ...prev, mobile_no: 'Please enter a valid mobile number' }));
                } else if (updatedValue === '0000000000') {
                    setErrors((prev) => ({ ...prev, mobile_no: 'Invalid mobile number' }));
                }
                else if (updatedValue.length < 10) {
                    setErrors((prev) => ({ ...prev, mobile_no: '10-digit required' }));
                }
                else if (updatedValue.length > 10) {
                    setErrors((prev) => ({ ...prev, mobile_no: 'Please enter a valid 10-digit mobile number' }));
                }
                else if (updatedValue.startsWith('0')) {
                    setErrors((prev) => ({ ...prev, mobile_no: 'Please check the number you have entered. It starts with "0".' }));
                } else {
                    setErrors((prev) => ({ ...prev, mobile_no: '' }));
                }
                break;

            default:
                break;
        }

        setFormData({
            ...formData,
            [name]: updatedValue,
        });
    };

    const handleSkipFunctionality = () => {
        navigate('/', {
            state: { returnUrl },
        }
        )
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (errors.mobile_no !== '' || null) {
            return
        }
        if (!isValidForm()) {
            return; // Prevent submission if the form is invalid
        }
        setLoading(true);
        setError(null); // Clear any previous error

        try {

            const registerdata = {
                ...formData,
                pincode: city_pincode || ""
            };

            const isExistingUser = !!loggedUser.authToken;

            const resRegistration = isExistingUser
                ? await API.API_V1.auth.postProfileUpdate(registerdata)
                : await API.API_V1.auth.postRegister(registerdata);


            pushUserRegistrationEvent(formData);

            const resJson = resRegistration?.data;

            if (resJson?.result?.id) {

                dispatch(updateProfileCompletedTag({
                    isProfileComplete: true,
                    userName: resJson.result.name,
                    email: resJson.result.email,
                }));
                handleLogin?.(true, resJson?.result?.id, resJson.result.mobile_no || mobileNo);


                const rawMobile = authInfo.mobileNumber;
                const cleanMobile = rawMobile?.replace(/"/g, "") || "";

                // if (cleanMobile) {
                //     // await handleSkipFunctionality();
                // }
                //  else {
                if (setCurrentView && origin == 'register') {
                    setCurrentView('verifyOtp')
                    return;
                } else if (origin == "register") {
                    navigate("/verify-otp", { state: { user_id: resJson.result.id, } });

                } else if (origin == 'verifyOtp') {
                    isRegistrationCompleted?.(true)
                } else if (origin = 'loginPage') {
                    navigate("/", { replace: true });
                } else {
                    navigate("/", { replace: true });
                }


                window.scrollTo({ top: 0, behavior: "smooth" });
                // }

            } else {
                setError(resJson?.message || "Registration failed");
                isRegistrationCompleted?.(false)
            }

        } catch (err: any) {

            const backendMessage =
                err?.name ||
                err?.message ||
                err?.error ||
                err?.result?.message ||
                (err?.status === 422
                    ? "Email or Mobile already registered"
                    : "Registration failed");



            setError(backendMessage);

        } finally {
            setLoading(false);
        }


    };


    useEffect(() => {
        if (nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit(e); // Call handleSubmit when Enter is pressed
        }
    };

    const LoadingSpinner = () => {
        return (
            <>
                <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                ></span>
                <span className="p-2" role="status">
                    Verifying...
                </span>
            </>
        );
    };

    return (
        <div>
            <form className="register-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                <div className="input-group">
                    <label className="register-label">Your Name<p className="required">*</p></label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        ref={nameInputRef}
                        maxLength={50} // Set maximum character limit to 50
                        className={`register-input ${errors.name ? 'input-error' : ''}`}
                    />
                    {errors.name && <span className="error-message-reg">{errors.name}</span>}
                </div>

                <div className="input-group">
                    <label className="register-label">Mobile No.<p className="required">*</p></label>
                    <input
                        type="tel"
                        name="mobile_no"
                        value={formData.mobile_no}
                        onChange={handleChange}
                        placeholder="99999 99999"
                        maxLength={14}
                        className={`register-input ${errors.mobile_no ? 'input-error' : ''}`}
                        disabled={formData.mobile_no === mobileNo && !!mobileNo}
                        autoComplete="off"
                    />
                    <span className='nochang_mobile'>you can not change mobile number here</span>
                    {errors.mobile_no && <span className="error-message-reg">{errors.mobile_no}</span>}
                </div>
                <div className="input-group">
                    <label className="register-label">E-Mail ID<p className="required">*</p></label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        maxLength={50}
                        onKeyDown={(e) => {
                            if (e.key === ' ') e.preventDefault(); // Prevent spaces
                        }}
                        placeholder="Example@email.com"
                        className={`register-input ${errors.email ? 'input-error' : ''}`}
                    />
                    {errors.email && <span className="error-message-reg">{errors.email}</span>}
                </div>
                <div className="input-group">
                    <label className="register-label">Referral Code</label>
                    <input
                        type="text"
                        name="referral_code"
                        value={formData.referral_code}
                        onChange={handleChange}
                        maxLength={50}
                        onKeyDown={(e) => {
                            if (e.key === " ") e.preventDefault();
                        }}
                        placeholder={refCode || "Referral code"}
                        className={`register-input ${errors.referral_code ? "input-error" : ""}`}
                        readOnly={formData.referral_code === refCode && !!refCode}
                    />
                    {errors.referral_code && <span className="error-message-reg">{errors.referral_code}</span>}
                </div>
                {error && <p className="error-message-req-fail">{error}</p>}
                <button
                    type="submit"
                    className={`register-button ${loading || !formData.name || !formData.email ? "disabled" : ""
                        }`}
                    disabled={loading || !formData.name || !formData.email}
                >
                    {loading
                        ? LoadingSpinner()
                        : (formData.mobile_no === mobileNo && !!mobileNo)
                            ? 'Complete Your Profile'
                            : 'Next'}
                </button>

            </form>
        </div>
    );
};

export default Register;


